import React, { useState, useMemo, useEffect, useRef, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { Config } from '@/config';
import {
  errorModalTextRecoil,
  completeModalRecoil,
  deleteModalRecoil,
} from '@/atom/ModalAtom';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import LS from '@/styles/layoutStyled';
import CS from '@/styles/componentStyled';
import BS from '@/styles/btnStyled';
import IS from '@/styles/inputStyled';
import Pagenation from '@/component/Pagenation';
import SearchBtn from '@/component/SearchBtn';
import DeleteModal from '@/modal/DeleteModal';
import LabelType1 from '@/component/LabelType1';
import { DeviceApi } from '@/sdk/DeviceApi';
import {
  DeviceControllerGetDeviceParams,
  DeviceControllerGetOriginalDataParams,
  DeleteDeviceOriginalDtoTx,
  SensorDataDtoRx,
  OriginalDataDtoRx,
} from '@/sdk/data-contracts';
import { deviceSensorDataListRecoil } from '@/atom/DeviceAtom';
import Chart from 'react-apexcharts';

const formatDate = (date: Date) => {
  return date.toISOString().slice(0, 10);
};

const formatDate2 = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const DataGraph = () => {
  const { id } = useParams<{ id: string }>();
  const deviceApi = new DeviceApi({ baseURL: Config.API_URL });
  const navigate = useNavigate();
  const today = formatDate(new Date());

  const setErrorModalText = useSetRecoilState(errorModalTextRecoil);
  const setCompleteModalState = useSetRecoilState(completeModalRecoil);
  const [deleteModalState, setDeleteModalState] =
    useRecoilState(deleteModalRecoil);
  const [deviceSensorDataListState, setDeviceSensorDataListState] =
    useRecoilState(deviceSensorDataListRecoil);

  const [startDate, setStartDate] = useState<string>(today);
  const [endDate, setEndDate] = useState<string>(today);
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [order, setOrder] = useState<string>('desc');
  const [sensorDatas, setSensorDatas] = useState<SensorDataDtoRx[]>([]);
  const [textDatas, setTextDatas] = useState<OriginalDataDtoRx[]>([]);
  const [dateArray, setdateArray] = useState<string[]>([today, today]);

  const sensorKeys =
    sensorDatas.length > 0 ? Object.keys(sensorDatas[0].datas) : [];
  const sensorSeries = sensorKeys.map((key) => ({
    name: key,
    data: sensorDatas
      .slice()
      .reverse()
      .map((item) => item.datas[key]),
  }));
  const sensorOptions: ApexCharts.ApexOptions = {
    chart: {
      dropShadow: {
        enabled: true,
        color: '#000',
        top: 18,
        left: 7,
        blur: 10,
        opacity: 0.2,
      },
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
    },
    colors: [
      '#77B6EA',
      '#545454',
      '#FF6347',
      '#32CD32',
      '#FFD700',
      '#6A5ACD',
      '#FF69B4',
      '#8A2BE2',
      '#40E0D0',
      '#FFA500',
    ],
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: 'smooth',
    },
    title: {
      text: '　',
      align: 'left',
    },
    grid: {
      borderColor: '#e7e7e7',
      row: {
        colors: ['#f3f3f3', 'transparent'],
        opacity: 0.5,
      },
    },
    markers: {
      size: 1,
    },
    xaxis: {
      labels: {
        show: false,
      },
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
      floating: true,
      offsetY: -25,
      offsetX: -5,
    },
  };

  const textKeys = textDatas.length > 0 ? Object.keys(textDatas[0].datas) : [];
  const textSeries = textKeys.map((key) => ({
    name: key,
    data: textDatas
      .slice()
      .reverse()
      .map((item) => item.datas[key]),
  }));
  const textOptions: ApexCharts.ApexOptions = {
    chart: {
      dropShadow: {
        enabled: true,
        color: '#000',
        top: 18,
        left: 7,
        blur: 10,
        opacity: 0.2,
      },
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
    },
    colors: [
      '#77B6EA',
      '#545454',
      '#FF6347',
      '#32CD32',
      '#FFD700',
      '#6A5ACD',
      '#FF69B4',
      '#8A2BE2',
      '#40E0D0',
      '#FFA500',
    ],
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: 'smooth',
    },
    title: {
      text: '　',
      align: 'left',
    },
    grid: {
      borderColor: '#e7e7e7',
      row: {
        colors: ['#f3f3f3', 'transparent'],
        opacity: 0.5,
      },
    },
    markers: {
      size: 1,
    },
    xaxis: {
      labels: {
        show: false,
      },
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
      floating: true,
      offsetY: -25,
      offsetX: -5,
    },
  };

  const filterSensorDataList = async () => {
    const dto: DeviceControllerGetDeviceParams = {
      id: Number(id),
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
      sortBy,
      order,
    };

    try {
      const { data } = await deviceApi.deviceControllerGetDevice(dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      setSensorDatas(data.sensorData);

      const dates = data.sensorData.map((item) =>
        new Date(item.createdAt).getTime(),
      );
      const minDate = new Date(Math.min(...dates));
      const maxDate = new Date(Math.max(...dates));

      setdateArray([formatDate2(minDate), formatDate2(maxDate)]);
    } catch (err: any) {}
  };

  const filterTextDataList = async () => {
    const dto: DeviceControllerGetOriginalDataParams = {
      id: Number(id),
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
      sortBy,
      order,
    };

    try {
      const { data } = await deviceApi.deviceControllerGetOriginalData(dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      setTextDatas(data.originalData);

      const dates = data.originalData.map((item) =>
        new Date(item.createdAt).getTime(),
      );
      const minDate = new Date(Math.min(...dates));
      const maxDate = new Date(Math.max(...dates));

      setdateArray([formatDate2(minDate), formatDate2(maxDate)]);
    } catch (err: any) {}
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (startDate === '' || endDate === '') {
      setErrorModalText('시작일 혹은 종료일을 선택해 주세요.');
      return;
    }

    filterSensorDataList();
    filterTextDataList();
  };

  const handleDataReset = () => {
    setStartDate(today);
    setEndDate(today);
    setdateArray([today, today]);
  };

  useEffect(() => {
    filterSensorDataList();
    filterTextDataList();
  }, []);

  return (
    <>
      <TabCntBox>
        <TableOpitonBox>
          <h1>센서 데이터</h1>
          <div className="right-box">
            <form onSubmit={handleSearch}>
              <InputArea>
                <h2>검색 기간</h2>
                <IS.TextInputType1
                  style={{ width: 145 }}
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    if (endDate !== '') {
                      if (e.target.value > endDate) {
                        setErrorModalText(
                          '시작일은 종료일보다 클 수 없습니다.',
                        );
                        return;
                      }
                    }
                    setStartDate(e.target.value);
                  }}
                />
                <h2>~</h2>
                <IS.TextInputType1
                  style={{ width: 145 }}
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    if (startDate !== '') {
                      if (e.target.value < startDate) {
                        setErrorModalText(
                          '종료일은 시작일보다 클 수 없습니다.',
                        );
                        return;
                      }
                    }
                    setEndDate(e.target.value);
                  }}
                />
                <SearchBtn />
              </InputArea>
            </form>
            <BS.Btn1Type5 type="button" onClick={handleDataReset}>
              초기화
            </BS.Btn1Type5>
          </div>
        </TableOpitonBox>
        <GraphBox>
          {sensorDatas.length !== 0 && (
            <Chart
              options={sensorOptions}
              series={sensorSeries}
              type="line"
              width="100%"
              height="100%"
            />
          )}
          {sensorDatas.length !== 0 && (
            <ul className="data-box">
              {dateArray.map((list, index) => (
                <li key={index}>{list}</li>
              ))}
            </ul>
          )}
          {sensorDatas.length === 0 && (
            <p>표시할 데이터가 존재하지 않습니다.</p>
          )}
        </GraphBox>
        <TableOpitonBox2>
          <h1>원본 데이터</h1>
        </TableOpitonBox2>
        <GraphBox>
          {textDatas.length !== 0 && (
            <Chart
              options={textOptions}
              series={textSeries}
              type="line"
              width="100%"
              height="100%"
            />
          )}
          {textDatas.length !== 0 && (
            <ul className="data-box">
              {dateArray.map((list, index) => (
                <li key={index}>{list}</li>
              ))}
            </ul>
          )}
          {textDatas.length === 0 && <p>표시할 데이터가 존재하지 않습니다.</p>}
        </GraphBox>
      </TabCntBox>
    </>
  );
};

export default DataGraph;

export const CntBox1 = styled(CS.CntBoxType1)`
  width: 100%;
`;

export const CntBox2 = styled(CS.CntBoxType1)`
  width: 100%;
  margin-top: 20px;
`;

export const InforWrap = styled.div`
  width: 100%;
  display: flex;
  gap: 20px;
  margin-top: 16px;
`;

export const InforBox1 = styled(CS.CntBoxType2)`
  position: relative;
  width: calc(50% - 13.3333px);
  > .device-btn {
    position: absolute;
    right: 20px;
    bottom: 20px;
  }
`;

export const InforBox2 = styled(CS.CntBoxType2)`
  width: calc(25% - 13.3333px);
  > .text-wrap {
    &:before {
      display: none;
    }
    > .text-box {
      width: 100%;
      &:first-child {
        padding-right: 0;
      }
      &:last-child {
        padding-left: 0;
      }
    }
  }
`;

export const InforBox3 = styled(CS.CntBoxType2)`
  width: calc(25% - 13.3333px);
  > .img-area {
    width: 100%;
    position: relative;
    margin-top: 12px;
    > button {
      position: absolute;
      width: 34px;
      height: 34px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
      border: 1px solid #ececec;
      background: #fff;
      border-radius: 4px;
      left: 10px;
      bottom: 10px;
      > .img-box {
        width: 24px;
      }
    }
  }
`;

export const SearchOptionBox = styled(CS.SearchOptionBox)`
  width: 100%;
  margin-top: 20px;
`;

export const InputWrap = styled.div`
  gap: 8px;
  display: flex;
  flex-direction: column;
`;

export const InputArea = styled.div`
  gap: 8px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  > h2 {
    color: #555;
    font-size: 14px;
  }
`;

export const TabCntBox = styled.div`
  width: 100%;
  margin-top: 20px;
`;

export const TableOpitonBox = styled(CS.TableOpitonBox)`
  width: 100%;
  margin-top: 0;
`;

export const TableOpitonBox2 = styled(TableOpitonBox)`
  margin-top: 60px;
`;

export const TableBox = styled(CS.TableType1)`
  width: 100%;
  margin-top: 16px;
  margin-bottom: 32px;
`;

export const MapBox = styled.div`
  width: 100%;
  height: 400px;
  position: relative;
  border-radius: 12px;
  overflow: hidden;
`;

export const MapModal = styled.div`
  position: absolute;
  z-index: 10;
  top: 50%;
  left: 60%;
  transform: translate(-50%, -50%);
  width: 136px;
  border-radius: 4px;
  box-sizing: border-box;
  border: 1px solid #d9d9d9;
  box-shadow: 4px 4px 12px 0px rgba(255, 255, 255, 0.25);
  overflow: hidden;
  > .title-box {
    box-sizing: border-box;
    padding: 8px 12px;
    background: #e9e8e8;
    font-size: 16px;
    font-weight: 500;
  }
  > .text-box {
    box-sizing: border-box;
    padding: 12px;
    background: #fff;
    > .state-box {
      display: flex;
      align-items: center;
      gap: 8px;
      > .text {
        font-size: 15px;
      }
      > .line {
        width: 1px;
        height: 10px;
        background: #d9d9d9;
      }
    }
    > .date {
      margin-top: 6px;
      font-size: 12px;
      font-weight: 300;
      color: #777;
    }
    > button {
      margin-top: 8px;
      font-size: 14px;
      color: #3c8deb;
    }
  }
`;

export const MapTextModal = styled.div`
  position: absolute;
  z-index: 10;
  top: 50%;
  left: 70%;
  transform: translate(-50%, -50%);
  border-radius: 4px;
  box-sizing: border-box;
  border: 1px solid #d9d9d9;
  padding: 10px 16px;
  box-shadow: 4px 4px 12px 0px rgba(255, 255, 255, 0.25);
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  > p {
    font-size: 15px;
    white-space: nowrap;
  }
  > button {
    width: 10px;
    min-width: 10px;
  }
`;

export const GraphBox = styled.div`
  margin-top: 16px;
  width: 100%;
  height: 360px;
  box-sizing: border-box;
  border-top: 2px solid #222222;
  padding-top: 28px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  > .data-box {
    display: flex;
    justify-content: space-between;
    font-size: 14px;
    color: #333;
    box-sizing: border-box;
    padding-left: 100px;
  }
  > p {
    text-align: center;
    font-size: 15px;
    color: #555;
  }
`;
